import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { fetchManagedTracker, fetchAccountManager } from '../lib/managedOrder';
import { MANAGED_STAGES, stageIndexOf, isCancelled } from '../lib/managedStages';
import { getTrackingUrl } from '../lib/tracking';
import { waLink } from '../lib/maabarContact';
import { startTelrPayment, paymentStartErrorText } from '../lib/telrPay';
import { fetchPaymentQuote, stageAmountSar, feeCreditSar, formatSar, sarFixedNote } from '../lib/paymentQuote';
import { fetchRequestFee, payRequestFee, feeNoticeText, feeStatusText, feeCreditLabel } from '../lib/requestFee';
import { sb } from '../supabase';

// Trader's managed-order tracker (the approved premium design). Shows the 9-stage
// journey, the assigned account manager (+ WhatsApp), the curated offer at the
// offer stage (approve → deposit / negotiate → WhatsApp), the pre-shipping video,
// and the buyer-visible event feed. Comms happen on WhatsApp; the app is tracking
// + offer + payment.
const LOGO = '/email-logo.png';

const CSS = `
.mo-wrap{ --paper:#F7F4EE; --ink:#141210; --ink-soft:#3A342C; --muted:#6B655B; --faint:#8F887C; --hair:#DcD4C4; --hair-strong:#C8BEA8; --header:#0E0D0C; --bronze:#9A7B4F; --done:#2F5D3A;
  --serif:'Cormorant Garamond','Iowan Old Style',Georgia,serif; --num:'Palatino Linotype',Georgia,serif; background:var(--paper); border-radius:6px; overflow:hidden; box-shadow:0 30px 70px -30px rgba(20,18,16,0.4); }
.mo-wrap *{ box-sizing:border-box; }
.mo-head{ position:relative; background:var(--header); color:#F3EFE7; padding:26px 30px 22px; border-bottom:2px solid var(--bronze); overflow:hidden; }
.mo-head::before{ content:''; position:absolute; inset:0; background:radial-gradient(120% 150% at 50% -40%, rgba(255,255,255,0.12), transparent 60%); }
.mo-hrow{ position:relative; display:flex; justify-content:space-between; align-items:flex-start; gap:18px; flex-wrap:wrap; }
.mo-logo{ height:38px; filter:brightness(0) invert(1); }
.mo-kick{ font-size:9.5px; letter-spacing:0.3em; text-transform:uppercase; color:#B69669; text-align:end; }
.mo-ref{ font-family:var(--num); font-size:15px; margin-top:5px; text-align:end; letter-spacing:0.04em; }
.mo-pill{ display:inline-block; margin-top:9px; font-size:11px; font-weight:700; padding:5px 12px; border-radius:999px; background:rgba(154,123,79,0.22); color:#EbDcC2; border:1px solid rgba(182,150,105,0.5); }
.mo-title{ position:relative; margin-top:15px; font-family:var(--serif); font-size:26px; font-weight:600; }
.mo-mgr{ display:flex; align-items:center; gap:12px; padding:14px 30px; border-bottom:1px solid var(--hair); flex-wrap:wrap; }
.mo-av{ width:42px; height:42px; border-radius:50%; background:linear-gradient(135deg,#2b2b2b,#4a4640); color:#EbDcC2; display:flex; align-items:center; justify-content:center; font-weight:700; }
.mo-mk{ font-size:9.5px; letter-spacing:0.14em; text-transform:uppercase; color:var(--bronze); }
.mo-mn{ font-size:14px; font-weight:600; color:var(--ink); }
.mo-ms{ font-size:11.5px; color:var(--muted); }
.mo-wa{ margin-inline-start:auto; display:inline-flex; align-items:center; gap:7px; background:#25D366; color:#fff; border-radius:9px; padding:9px 15px; font-size:12.5px; font-weight:600; text-decoration:none; }
.mo-body{ padding:24px 30px 30px; }
.mo-sl{ font-size:10px; font-weight:700; letter-spacing:0.2em; text-transform:uppercase; color:var(--bronze); margin-bottom:18px; }
.mo-stage{ position:relative; padding-inline-start:38px; padding-bottom:24px; }
.mo-stage:last-child{ padding-bottom:0; }
.mo-stage::before{ content:''; position:absolute; inset-inline-start:12px; top:24px; bottom:-2px; width:2px; background:var(--hair); }
.mo-stage:last-child::before{ display:none; }
.mo-stage.done::before{ background:var(--done); }
.mo-node{ position:absolute; inset-inline-start:2px; top:2px; width:22px; height:22px; border-radius:50%; background:var(--paper); border:2px solid var(--hair-strong); display:flex; align-items:center; justify-content:center; font-size:12px; color:var(--faint); }
.mo-stage.done .mo-node{ background:var(--done); border-color:var(--done); color:#fff; }
.mo-stage.active .mo-node{ border-color:var(--bronze); box-shadow:0 0 0 4px rgba(154,123,79,0.15); }
.mo-stage.active .mo-node::after{ content:''; width:8px; height:8px; border-radius:50%; background:var(--bronze); }
.mo-st{ font-size:15px; font-weight:600; color:var(--ink); }
.mo-stage.upcoming .mo-st{ color:var(--faint); font-weight:500; }
.mo-when{ font-size:11px; color:var(--faint); margin-top:2px; }
.mo-ev{ font-size:12.5px; color:var(--muted); margin-top:5px; line-height:1.55; }
.mo-ev b{ color:var(--ink-soft); font-weight:600; }
.mo-video{ margin-top:10px; border:1px solid var(--hair); border-radius:10px; overflow:hidden; background:#000; max-width:360px; }
.mo-video video{ width:100%; display:block; }
.mo-track{ display:inline-block; margin-top:10px; padding:9px 16px; border:1px solid var(--hair); border-radius:999px; font-size:12.5px; font-weight:600; color:var(--text-primary); text-decoration:none; }
.mo-track:hover{ background:var(--bg-raised,rgba(0,0,0,0.04)); }
.mo-offer{ margin-top:12px; border:1px solid var(--hair-strong); border-radius:12px; overflow:hidden; background:#fff; max-width:420px; }
.mo-otop{ padding:11px 15px; background:rgba(154,123,79,0.07); border-bottom:1px solid var(--hair); display:flex; justify-content:space-between; align-items:center; }
.mo-otop .k{ font-size:10px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--bronze); }
.mo-ofac{ font-size:11px; color:var(--ink-soft); }
.mo-orow{ display:flex; justify-content:space-between; padding:13px 15px; align-items:center; }
.mo-ok{ font-size:12px; color:var(--muted); }
.mo-ov{ font-family:var(--num); font-size:20px; font-weight:600; }
.mo-ov small{ font-size:11px; color:var(--muted); }
.mo-oref{ font-size:11px; color:var(--muted); padding:0 15px 12px; text-align:end; }
.mo-oact{ display:flex; gap:9px; padding:13px 15px; border-top:1px solid var(--hair); flex-wrap:wrap; }
.mo-approve{ flex:1; min-width:150px; background:var(--ink); color:var(--paper); border:none; border-radius:9px; padding:12px; font-size:14px; font-weight:600; cursor:pointer; }
.mo-approve:disabled{ opacity:0.55; cursor:default; }
.mo-neg{ background:none; border:1px solid var(--hair-strong); border-radius:9px; padding:12px 16px; font-size:13px; color:var(--ink); text-decoration:none; display:inline-flex; align-items:center; }
.mo-assure{ display:flex; gap:7px; padding:10px 15px; background:rgba(47,93,58,0.08); border-top:1px solid rgba(47,93,58,0.15); font-size:11.5px; color:#2c5236; }
.mo-fp{ display:grid; grid-template-columns:1fr 1fr; border-bottom:1px solid var(--hair); }
.mo-fpi{ padding:10px 15px; border-inline-end:1px solid var(--hair); border-bottom:1px solid var(--hair); }
.mo-fpi:nth-child(2n){ border-inline-end:none; }
.mo-fpi .l{ font-size:9px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--faint); }
.mo-fpi .v{ font-size:12.5px; color:var(--ink); margin-top:2px; }
.mo-certblock{ padding:10px 15px; border-bottom:1px solid var(--hair); }
.mo-certlabel{ font-size:9px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--faint); margin-bottom:6px; }
.mo-certs-in{ display:flex; flex-wrap:wrap; gap:5px; }
.mo-onote{ padding:10px 15px; border-bottom:1px solid var(--hair); font-size:12.5px; color:var(--muted); line-height:1.55; }
.mo-cert{ font-size:10.5px; color:var(--ink-soft); background:rgba(154,123,79,0.08); border:1px solid rgba(154,123,79,0.25); border-radius:5px; padding:2px 8px; }
.mo-photos{ display:flex; gap:6px; padding:10px 15px; overflow-x:auto; border-bottom:1px solid var(--hair); }
.mo-photos img{ width:64px; height:64px; object-fit:cover; border-radius:6px; border:1px solid var(--hair); flex-shrink:0; }
`;

const WA = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.8 14.01c-.24.68-1.42 1.3-1.95 1.34-.5.04-.5.4-3.15-.66-2.67-1.05-4.35-3.76-4.48-3.94-.13-.18-1.07-1.42-1.07-2.71 0-1.29.68-1.92.92-2.19.24-.26.52-.33.7-.33.17 0 .35 0 .5.01.16.01.38-.06.59.45.24.58.81 2 .88 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.56.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.37.27.14.43.12.59-.07.16-.19.68-.79.86-1.06.18-.27.36-.22.6-.13.24.09 1.53.72 1.8.85.27.13.44.2.5.31.07.11.07.63-.17 1.31z" /></svg>
);

export default function ManagedOrder({ lang = 'ar' }) {
  const isAr = lang === 'ar';
  const { id } = useParams();
  const nav = useNavigate();
  usePageTitle(isAr ? 'متابعة الطلب المُدار' : 'Managed order', lang);

  const [data, setData] = useState(null);
  const [mgr, setMgr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  // A payment that could not START (telr-create refused, or the network): shown
  // inline under the button that was pressed — 'offer' or 'fee' — never alert().
  const [payError, setPayError] = useState(null); // { where, title, body, detail }
  const [quote, setQuote] = useState(null);
  const [fee, setFee] = useState(null);

  const load = async () => {
    const d = await fetchManagedTracker(id);
    setData(d);
    // The request fee (299 SAR): until it is paid, the Maabar team has not
    // started — the tracker says so and offers the payment.
    setFee(await fetchRequestFee(sb, id));
    // The curated offer IS an order_invoice; what the trader pays for it comes
    // from payment_quote() — the same call telr-create charges from — at the
    // rate locked when the invoice was issued.
    setQuote(d.offer?.id ? await fetchPaymentQuote(sb, { invoiceId: d.offer.id }) : null);
    if (d.request?.assigned_to) setMgr(await fetchAccountManager(d.request.assigned_to));
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = {
    kicker: isAr ? 'الطلب المُدار' : 'Managed order', managing: isAr ? 'قيد المتابعة · مَعبر تدير كل خطوة' : 'In progress · Maabar manages every step',
    cancelled: isAr ? 'أُلغي الطلب' : 'Cancelled',
    mgrK: isAr ? 'مدير حسابك' : 'Your account manager', mgrS: isAr ? 'يتابع طلبك خطوة بخطوة' : 'Follows your order step by step',
    mgrDefault: isAr ? 'فريق مَعبر' : 'Maabar team', wa: isAr ? 'واتساب' : 'WhatsApp',
    journey: isAr ? 'رحلة طلبك' : 'Your order journey',
    offerK: isAr ? 'عرض مَعبر المنسّق' : "Maabar's curated offer", fac: isAr ? 'مصنع مُعتمد من مَعبر' : 'A Maabar-vetted factory',
    total: isAr ? 'الإجمالي المستحق' : 'Total due', approve: isAr ? 'وافق وادفع العربون' : 'Approve & pay deposit', payFull: isAr ? 'ادفع كامل' : 'Pay in full',
    negotiate: isAr ? 'أريد التفاوض' : 'Negotiate', assure: isAr ? 'لا نبدأ الإنتاج إلا بموافقتك — أموالك محفوظة لدى مَعبر حتى تستلم طلبك.' : "We only start once you approve — your funds are held by Maabar until you receive your order.",
    loading: isAr ? 'جارٍ التحميل…' : 'Loading…', notFound: isAr ? 'الطلب غير موجود.' : 'Order not found.',
    back: isAr ? '→ لوحتي' : '← My dashboard',
    track: isAr ? 'تتبّع الشحنة' : 'Track shipment',
  };

  if (loading) return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: 'var(--text-secondary)' }}>{t.loading}</div>;
  if (!data?.request) return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: 'var(--text-secondary)' }}>{t.notFound}</div>;

  const r = data.request;
  const offer = data.offer;
  const events = data.events || [];
  const title = (isAr ? r.title_ar : r.title_en) || r.title_ar || r.title_en || '';
  const currentIdx = stageIndexOf(r.managed_status);
  const finished = currentIdx === MANAGED_STAGES.length - 1;   // delivered
  // Only build a tracking link when ops captured something; a bare carrier
  // name with no number would otherwise produce a useless 17track lookup.
  const trackUrl = (r.tracking_number || r.tracking_url)
    ? getTrackingUrl(r.shipping_company, r.tracking_number, r.tracking_url)
    : '';
  const cancelled = isCancelled(r.managed_status);
  const eventsByStage = events.reduce((acc, e) => { (acc[e.stage || 'received'] = acc[e.stage || 'received'] || []).push(e); return acc; }, {});

  // When a stage was reached.
  //
  // `ar-u-ca-gregory` PINS the calendar rather than leaving it to the runtime's
  // locale data, which is what varies: ICU has mapped ar-SA to Hijri in some
  // builds and Gregorian in others, and a delivery date that silently switches
  // calendars between browsers is not a difference a trader would read as a
  // rendering setting. Measured in this repo's Node (2026-09-15):
  //   ar-u-ca-gregory -> "15 سبتمبر 2026، 09:22 ص"   (Gregorian, Western digits)
  //   ar-SA           -> "١٥ سبتمبر ٢٠٢٦"            (Gregorian, Arabic-Indic)
  // So the two differ here in NUMERALS, not calendar. Pinning costs nothing and
  // removes the runtime from the question either way.
  const whenText = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(isAr ? 'ar-u-ca-gregory' : 'en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };
  const mgrName = mgr?.full_name || t.mgrDefault;

  // Offer money: SAR only, from payment_quote(). The rate and the supplier's
  // own-currency figure are internal (owner, 2026-09-29) — the trader reads
  // the SAR total and that it is fixed from the moment the invoice was issued.
  // A quote that is not payable (CNY, or a failed call) shows no amount and no
  // Pay button, never the invoice's USD/CNY figure.
  const payable = Boolean(quote?.payable);
  const offerRef = payable ? sarFixedNote('invoice', isAr ? 'ar' : 'en') : '';
  const fp = offer?.factory_profile;   // anonymized factory profile (no name/contact)

  const pay = async (stage) => {
    if (!offer) return;
    setPaying(true);
    setPayError(null);
    try { await startTelrPayment(offer.id, stage, 'invoice'); }   // redirects to Telr on success
    catch (e) {
      console.error('[ManagedOrder] could not start the offer payment:', e?.status || '', e?.message || e);
      setPayError({ where: 'offer', ...paymentStartErrorText(e, lang) });
      setPaying(false);
    }
  };

  // The request fee credit on the first payment. When it covers the whole
  // deposit there is no deposit to pay — the trader pays the rest in full.
  const creditSar = feeCreditSar(quote);
  const wantsFull = String(r.payment_plan) === '100' || stageAmountSar(quote, 'deposit') === null;
  const dueNow = stageAmountSar(quote, wantsFull ? 'full' : 'deposit');
  const feeAwaiting = fee?.status === 'awaiting_payment' && String(r.status || '').toLowerCase() === 'open';
  const payFee = async () => {
    setPaying(true);
    setPayError(null);
    try { await payRequestFee(r.id); }   // redirects to Telr on success
    catch (e) {
      console.error('[ManagedOrder] could not start the fee payment:', e?.status || '', e?.message || e);
      setPayError({ where: 'fee', ...paymentStartErrorText(e, lang) });
      setPaying(false);
    }
  };

  const payErrorBox = (where) => (payError?.where === where ? (
    <div role="alert" style={{ margin: '0 15px 12px', padding: '11px 13px', borderRadius: 10, background: 'rgba(138,59,46,0.08)', border: '1px solid rgba(138,59,46,0.35)', color: '#141210' }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#8A3B2E', marginBottom: 3 }}>{payError.title}</div>
      <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>{payError.body}</div>
      {payError.detail && <div dir="ltr" style={{ fontSize: 11, color: '#6B655B', marginTop: 5 }}>{payError.detail}</div>}
    </div>
  ) : null);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '22px 14px 60px' }} dir={isAr ? 'rtl' : 'ltr'}>
      <button onClick={() => nav('/dashboard?tab=requests')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12 }}>{t.back}</button>
      <div className="mo-wrap">
        <style>{CSS}</style>
        <div className="mo-head">
          <div className="mo-hrow">
            <img className="mo-logo" src={LOGO} alt="MAABAR" />
            <div>
              <div className="mo-kick">{t.kicker}</div>
              <div className="mo-ref">{r.request_ref || r.id?.slice(0, 8)}</div>
              <div className="mo-pill">{cancelled ? t.cancelled : t.managing}</div>
            </div>
          </div>
          <div className="mo-title">{title}</div>
        </div>

        <div className="mo-mgr">
          <div className="mo-av">{(mgrName || 'م').trim().charAt(0)}</div>
          <div>
            <div className="mo-mk">{t.mgrK}</div>
            <div className="mo-mn">{mgrName}</div>
            <div className="mo-ms">{t.mgrS}</div>
          </div>
          <a className="mo-wa" href={waLink(`استفسار عن طلبي المُدار رقم ${r.request_ref || r.id}`)} target="_blank" rel="noopener noreferrer">{WA} {t.wa}</a>
        </div>

        {feeAwaiting && (
          <div className="mo-body" style={{ paddingBottom: 0 }}>
            <div className="mo-offer">
              <div className="mo-otop"><span className="k">{isAr ? 'رسوم تأكيد الطلب' : 'Request confirmation fee'}</span></div>
              <div className="mo-onote">{feeNoticeText(isAr ? 'ar' : 'en', fee.amount)}</div>
              <div className="mo-oact">
                <button className="mo-approve" disabled={paying} onClick={payFee}>
                  {isAr ? `ادفع الرسوم — ${formatSar(fee.amount)} ر.س` : `Pay the fee — SAR ${formatSar(fee.amount)}`}
                </button>
              </div>
              {payErrorBox('fee')}
            </div>
          </div>
        )}
        {fee && !feeAwaiting && feeStatusText(fee.status, isAr ? 'ar' : 'en') && (
          <div className="mo-oref" style={{ paddingTop: 10 }}>{feeStatusText(fee.status, isAr ? 'ar' : 'en')}</div>
        )}

        <div className="mo-body">
          <div className="mo-sl">{t.journey}</div>
          {MANAGED_STAGES.map((stg, i) => {
            // The last stage has nothing after it, so "before the current one"
            // never ticked it: a delivered order sat "in progress" forever.
            const isDone = i < currentIdx || (finished && i === currentIdx);
            const cls = isDone ? 'done' : i === currentIdx ? 'active' : 'upcoming';
            const evs = eventsByStage[stg.key] || [];
            const video = evs.find((e) => e.kind === 'video' && e.media_url);
            // A 'status' event carries no information the heading above does not
            // already give: advance() writes its title AS the stage name, so
            // rendering it repeated "Sourcing" under the heading "Sourcing",
            // once per advance. What it does carry is WHEN the stage was
            // reached, so the latest one becomes the stage's timestamp and the
            // line itself goes.
            //
            // The events are NOT removed and their kind is NOT changed: 'status'
            // is what wa_on_managed_event fires on (migration 20260913230454),
            // and touching either would silence every stage message. This is a
            // rendering decision only.
            const statuses = evs.filter((e) => e.kind === 'status');
            const reachedAt = statuses.length ? statuses[statuses.length - 1].created_at : null;
            const detail = evs.filter((e) => e.kind !== 'video' && e.kind !== 'status');
            return (
              <div key={stg.key} className={`mo-stage ${cls}`}>
                <div className="mo-node">{isDone ? '✓' : ''}</div>
                <div className="mo-st">{isAr ? stg.ar : stg.en}</div>
                {reachedAt && <div className="mo-when">{whenText(reachedAt)}</div>}
                {detail.map((e) => (
                  <div key={e.id} className="mo-ev">{e.title ? <b>{e.title}{e.body ? ' — ' : ''}</b> : null}{e.body}</div>
                ))}
                {video && (
                  <div className="mo-video"><video src={video.media_url} controls playsInline /></div>
                )}

                {/* Carrier tracking — only once the order is out for local delivery,
                    and only when ops actually captured a number or a manual link. */}
                {stg.key === 'out_for_delivery' && trackUrl && (
                  <a className="mo-track" href={trackUrl} target="_blank" rel="noreferrer">
                    {t.track}{r.tracking_number ? ` · ${r.tracking_number}` : ''}
                  </a>
                )}
                {/* Curated offer at the offer stage */}
                {stg.key === 'offer' && offer && offer.status !== 'paid' && (
                  <div className="mo-offer">
                    <div className="mo-otop"><span className="k">{t.offerK}</span><span className="mo-ofac">{t.fac}</span></div>
                    {fp && (
                      <>
                        <div className="mo-fp">
                          {fp.city && <div className="mo-fpi"><div className="l">{isAr ? 'الموقع' : 'Location'}</div><div className="v">{fp.city}</div></div>}
                          {fp.years && <div className="mo-fpi"><div className="l">{isAr ? 'الخبرة' : 'Experience'}</div><div className="v">{fp.years} {isAr ? 'سنة' : 'yrs'}</div></div>}
                          {fp.rating && <div className="mo-fpi"><div className="l">{isAr ? 'التقييم' : 'Rating'}</div><div className="v">★ {fp.rating}</div></div>}
                          {fp.capacity && <div className="mo-fpi"><div className="l">{isAr ? 'الطاقة الإنتاجية' : 'Capacity'}</div><div className="v">{fp.capacity}</div></div>}
                          {fp.export_markets && <div className="mo-fpi"><div className="l">{isAr ? 'أسواق التصدير' : 'Export markets'}</div><div className="v">{fp.export_markets}</div></div>}
                          {fp.moq && <div className="mo-fpi"><div className="l">{isAr ? 'أقل كمية' : 'MOQ'}</div><div className="v">{fp.moq}</div></div>}
                          {fp.lead_time && <div className="mo-fpi"><div className="l">{isAr ? 'مدة التسليم المتوقعة' : 'Est. delivery'}</div><div className="v">{fp.lead_time}</div></div>}
                        </div>
                        {Array.isArray(fp.certifications) && fp.certifications.length > 0 && (
                          <div className="mo-certblock">
                            <div className="mo-certlabel">{isAr ? 'شهادات الجودة' : 'Quality certifications'}</div>
                            <div className="mo-certs-in">{fp.certifications.map((c, i) => <span key={i} className="mo-cert">{c}</span>)}</div>
                          </div>
                        )}
                        {Array.isArray(fp.photos) && fp.photos.length > 0 && (
                          <div className="mo-photos">{fp.photos.map((p, i) => <img key={i} src={p} alt="" />)}</div>
                        )}
                        {offer.notes && (
                          <div className="mo-onote"><div className="mo-certlabel">{isAr ? 'ملاحظات' : 'Notes'}</div>{offer.notes}</div>
                        )}
                      </>
                    )}
                    <div className="mo-orow"><span className="mo-ok">{t.total}</span><span className="mo-ov">{payable ? <>{formatSar(quote.total_sar)}<small> SAR</small></> : <small>—</small>}</span></div>
                    {payable && creditSar > 0 && (
                      <>
                        <div className="mo-orow"><span className="mo-ok">{feeCreditLabel(isAr ? 'ar' : 'en')}</span><span className="mo-ov" style={{ fontSize: 15 }}>−{formatSar(creditSar)}<small> SAR</small></span></div>
                        {dueNow !== null && (
                          <div className="mo-orow"><span className="mo-ok">{wantsFull ? (isAr ? 'المستحق الآن' : 'Due now') : (isAr ? 'العربون المستحق الآن' : 'Deposit due now')}</span><span className="mo-ov">{formatSar(dueNow)}<small> SAR</small></span></div>
                        )}
                      </>
                    )}
                    {offerRef ? <div className="mo-oref">{offerRef}</div> : null}
                    {!payable && (
                      <div className="mo-oref">{isAr ? 'الدفع الإلكتروني غير متاح لهذا العرض بعد — تواصل مع مديرك في مَعبر.' : "This offer can't be paid online yet — contact your Maabar manager."}</div>
                    )}
                    <div className="mo-oact">
                      {payable && (
                        <button className="mo-approve" disabled={paying} onClick={() => pay(wantsFull ? 'full' : 'deposit')}>
                          {wantsFull ? t.payFull : t.approve}
                        </button>
                      )}
                      <a className="mo-neg" href={waLink(`أريد التفاوض على العرض — الطلب رقم ${r.request_ref || r.id}`)} target="_blank" rel="noopener noreferrer">{t.negotiate}</a>
                    </div>
                    {payErrorBox('offer')}
                    <div className="mo-assure">🔒 {t.assure}</div>
                  </div>
                )}
                {stg.key === 'offer' && offer && offer.status === 'paid' && (
                  <div className="mo-ev"><b>{isAr ? '✓ تمت الموافقة والدفع' : '✓ Approved & paid'}</b></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
